(function () {
  'use strict';

  /* ── Helpers ── */
  function fmt(cents, currency) {
    try { return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100); }
    catch { return (cents / 100).toFixed(2) + ' ' + (currency || 'EUR'); }
  }

  function el(id) { return document.getElementById(id); }

  function getForm(root) {
    const sel = root.getAttribute('data-form');
    return sel ? document.querySelector(sel) : root.closest('form');
  }

  function getQty(form) {
    const q = form && form.querySelector('input[name="quantity"]');
    const v = q ? parseInt(q.value, 10) : 1;
    return isNaN(v) || v < 1 ? 1 : v;
  }

  /* ── Init per configurator instance ── */
  function initConfigurator(root) {
    const sid = root.getAttribute('data-section-id');
    const currency = root.getAttribute('data-currency') || 'EUR';
    const strings = {
      minimum: root.getAttribute('data-string-minimum'),
      maximum: root.getAttribute('data-string-maximum'),
      wholeMillimeters: root.getAttribute('data-string-whole-millimeters'),
      minimumQuantity: root.getAttribute('data-string-minimum-quantity'),
      pieces: root.getAttribute('data-string-pieces'),
      adjustQuantity: root.getAttribute('data-string-adjust-quantity'),
    };
    const form = getForm(root);

    // Load variant data from embedded JSON
    const dataScript = el('gc-variant-data-' + sid);
    let variants = [];
    try { variants = JSON.parse(dataScript.textContent); } catch (e) { console.error('GC: failed to parse variant data', e); }

    // DOM refs
    const wInput = el('gc-width-' + sid);
    const hInput = el('gc-height-' + sid);
    const wError = el('gc-width-error-' + sid);
    const hError = el('gc-height-error-' + sid);
    const wRange = el('gc-width-range-' + sid);
    const hRange = el('gc-height-range-' + sid);
    const infoBox = el('gc-info-' + sid);
    const areaDisplay = el('gc-area-display-' + sid);
    const ppm2Display = el('gc-ppm2-display-' + sid);
    const areaPriceDisplay = el('gc-area-price-display-' + sid);
    const minAreaRow = el('gc-minarea-row-' + sid);
    const minAreaPriceDisplay = el('gc-minarea-price-display-' + sid);
    const lfmRow = el('gc-lfm-row-' + sid);
    const lfmRate = el('gc-lfm-rate-' + sid);
    const lfmPriceDisplay = el('gc-lfm-price-display-' + sid);
    const totalDisplay = el('gc-total-display-' + sid);
    const minQtyNotice = el('gc-min-qty-' + sid);

    // Hidden inputs
    const areaHidden = el('gc-area-' + sid);
    const calcPriceHidden = el('gc-calc-price-' + sid);
    const unitPriceHidden = el('gc-unit-price-' + sid);
    const lfmTotalHidden = el('gc-lfm-total-' + sid);

    // Preview SVG refs
    const previewWrap = el('gc-preview-' + sid);
    const previewFace = el('gc-face-' + sid);
    const previewEdgeTop = el('gc-edge-top-' + sid);
    const previewEdgeRight = el('gc-edge-right-' + sid);
    const previewDimW = el('gc-dim-w-' + sid);
    const previewDimH = el('gc-dim-h-' + sid);
    const previewPlaceholder = el('gc-preview-placeholder-' + sid);

    let currentVariant = null;

    /* ── Get current variant from form ── */
    function getCurrentVariant() {
      if (!form) return variants[0] || null;
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return variants[0] || null;
      const vid = parseInt(idInput.value, 10);
      return variants.find(v => v.id === vid) || variants[0] || null;
    }

    /* ── Update dimension limits from variant metafields ── */
    function updateLimits() {
      currentVariant = getCurrentVariant();
      if (!currentVariant) return;

      const v = currentVariant;
      wInput.min = v.min_w;
      wInput.max = v.max_w;
      hInput.min = v.min_h;
      hInput.max = v.max_h;

      wRange.textContent = v.min_w + '–' + v.max_w;
      hRange.textContent = v.min_h + '–' + v.max_h;

      // Show/hide LFM row
      if (v.has_lfm && v.lfm_price > 0) {
        lfmRow.style.display = '';
        lfmRate.textContent = fmt(v.lfm_price * 100, currency);
      } else {
        lfmRow.style.display = 'none';
      }

      // Min quantity notice
      if (v.has_min_quantity && v.min_quantity > 1) {
        minQtyNotice.style.display = '';
        minQtyNotice.textContent = strings.minimumQuantity + ' ' + v.min_quantity + ' ' + strings.pieces;
      } else {
        minQtyNotice.style.display = 'none';
      }
    }

    /* ── Validate inputs ── */
    function validate() {
      if (!currentVariant) return { valid: false, errors: [] };
      const v = currentVariant;
      const w = parseFloat(wInput.value);
      const h = parseFloat(hInput.value);
      const errors = [];
      let wValid = true, hValid = true;

      // Width validation
      if (!wInput.value || isNaN(w)) {
        wValid = false;
      } else if (w < v.min_w) {
        wError.textContent = strings.minimum + ' ' + v.min_w + ' mm';
        wInput.classList.add('gc__input--invalid');
        wValid = false;
      } else if (w > v.max_w) {
        wError.textContent = strings.maximum + ' ' + v.max_w + ' mm';
        wInput.classList.add('gc__input--invalid');
        wValid = false;
      } else if (w % 1 !== 0) {
        wError.textContent = strings.wholeMillimeters;
        wInput.classList.add('gc__input--invalid');
        wValid = false;
      }
      if (wValid) {
        wError.textContent = '';
        wInput.classList.remove('gc__input--invalid');
      }

      // Height validation
      if (!hInput.value || isNaN(h)) {
        hValid = false;
      } else if (h < v.min_h) {
        hError.textContent = strings.minimum + ' ' + v.min_h + ' mm';
        hInput.classList.add('gc__input--invalid');
        hValid = false;
      } else if (h > v.max_h) {
        hError.textContent = strings.maximum + ' ' + v.max_h + ' mm';
        hInput.classList.add('gc__input--invalid');
        hValid = false;
      } else if (h % 1 !== 0) {
        hError.textContent = strings.wholeMillimeters;
        hInput.classList.add('gc__input--invalid');
        hValid = false;
      }
      if (hValid) {
        hError.textContent = '';
        hInput.classList.remove('gc__input--invalid');
      }

      const bothFilled = wInput.value && hInput.value && !isNaN(w) && !isNaN(h);
      if (!bothFilled) return { valid: false, errors: [] };
      if (!wValid || !hValid) return { valid: false, errors: [] };

      // Area calculation
      const areaSqm = (w * h) / 1_000_000;

      // Quantity validation
      const qty = getQty(form);
      if (v.has_min_quantity && v.min_quantity > 1 && qty < v.min_quantity) {
        errors.push({ type: 'warn', msg: strings.minimumQuantity + ' ' + v.min_quantity + ' ' + strings.pieces + '. ' + strings.adjustQuantity });
      }

      return { valid: errors.length === 0 && wValid && hValid, errors, w, h, areaSqm, qty };
    }

    /* ── Update 2D preview ── */
    function updatePreview(w, h, isValid) {
      if (!previewWrap) return;
      const hasDims = w > 0 && h > 0;

      // Empty / no-input state
      if (!hasDims) {
        if (previewPlaceholder) previewPlaceholder.style.display = '';
        if (previewFace) { previewFace.setAttribute('width', '0'); previewFace.setAttribute('height', '0'); }
        if (previewEdgeTop) previewEdgeTop.setAttribute('points', '');
        if (previewEdgeRight) previewEdgeRight.setAttribute('points', '');
        if (previewDimW) previewDimW.style.display = 'none';
        if (previewDimH) previewDimH.style.display = 'none';
        previewWrap.classList.remove('gc__preview--invalid');
        return;
      }
      if (previewPlaceholder) previewPlaceholder.style.display = 'none';
      if (previewDimW) previewDimW.style.display = '';
      if (previewDimH) previewDimH.style.display = '';

      // Drawing area inside the 300×240 viewBox.
      const padLeft = 14;
      const padTop = 16;
      const padRight = 36;
      const padBottom = 40;
      const innerW = 300 - padLeft - padRight;
      const innerH = 240 - padTop - padBottom;

      // Fit the rectangle keeping aspect ratio.
      const aspect = w / h;
      let rectW, rectH;
      if (innerW / innerH > aspect) {
        rectH = innerH;
        rectW = rectH * aspect;
      } else {
        rectW = innerW;
        rectH = rectW / aspect;
      }
      const rectX = padLeft + (innerW - rectW) / 2;
      const rectY = padTop + (innerH - rectH) / 2;

      // Depth offset for fake 3D
      const dx = 4;
      const dy = 4;

      previewFace.setAttribute('x', rectX.toFixed(2));
      previewFace.setAttribute('y', rectY.toFixed(2));
      previewFace.setAttribute('width', rectW.toFixed(2));
      previewFace.setAttribute('height', rectH.toFixed(2));

      // Top thickness band
      previewEdgeTop.setAttribute('points', [
        rectX.toFixed(2) + ',' + rectY.toFixed(2),
        (rectX + rectW).toFixed(2) + ',' + rectY.toFixed(2),
        (rectX + rectW + dx).toFixed(2) + ',' + (rectY - dy).toFixed(2),
        (rectX + dx).toFixed(2) + ',' + (rectY - dy).toFixed(2)
      ].join(' '));

      // Right thickness band
      previewEdgeRight.setAttribute('points', [
        (rectX + rectW).toFixed(2) + ',' + rectY.toFixed(2),
        (rectX + rectW + dx).toFixed(2) + ',' + (rectY - dy).toFixed(2),
        (rectX + rectW + dx).toFixed(2) + ',' + (rectY + rectH - dy).toFixed(2),
        (rectX + rectW).toFixed(2) + ',' + (rectY + rectH).toFixed(2)
      ].join(' '));

      // Width dimension (below)
      const wLine = previewDimW.querySelector('line');
      const wText = previewDimW.querySelector('text');
      const wY = rectY + rectH + 14;
      wLine.setAttribute('x1', rectX.toFixed(2));
      wLine.setAttribute('y1', wY.toFixed(2));
      wLine.setAttribute('x2', (rectX + rectW).toFixed(2));
      wLine.setAttribute('y2', wY.toFixed(2));
      wText.setAttribute('x', (rectX + rectW / 2).toFixed(2));
      wText.setAttribute('y', (wY + 14).toFixed(2));
      wText.textContent = Math.round(w) + ' mm';

      // Height dimension (right)
      const hLine = previewDimH.querySelector('line');
      const hText = previewDimH.querySelector('text');
      const hX = rectX + rectW + dx + 8;
      hLine.setAttribute('x1', hX.toFixed(2));
      hLine.setAttribute('y1', (rectY - dy).toFixed(2));
      hLine.setAttribute('x2', hX.toFixed(2));
      hLine.setAttribute('y2', (rectY + rectH - dy).toFixed(2));
      const hCenterY = rectY - dy + rectH / 2;
      hText.setAttribute('x', hX.toFixed(2));
      hText.setAttribute('y', hCenterY.toFixed(2));
      hText.setAttribute('transform', 'rotate(-90 ' + hX.toFixed(2) + ' ' + hCenterY.toFixed(2) + ')');
      hText.textContent = Math.round(h) + ' mm';

      // State classes
      previewWrap.classList.toggle('gc__preview--invalid', !isValid);
    }

    /* ── Recalculate pricing ── */
    function recalc() {
      if (!currentVariant) return;
      const v = currentVariant;
      const result = validate();

      // Show info/warnings
      infoBox.innerHTML = '';
      for (const err of result.errors) {
        const div = document.createElement('div');
        div.className = 'gc__info-msg gc__info-msg--' + err.type;
        div.textContent = err.msg;
        infoBox.appendChild(div);
      }

      // Toggle ATC button
      const atcBtn = form && form.querySelector('[name="add"], .product-form__submit, button[type="submit"]');
      if (atcBtn) {
        if (result.valid) {
          atcBtn.classList.remove('gc-atc-disabled');
          atcBtn.removeAttribute('aria-disabled');
        } else {
          atcBtn.classList.add('gc-atc-disabled');
          atcBtn.setAttribute('aria-disabled', 'true');
        }
      }

      if (!result.w || !result.h) {
        // Clear displays
        areaDisplay.textContent = '—';
        ppm2Display.textContent = fmt(v.price, currency) + '/m²';
        areaPriceDisplay.textContent = '—';
        minAreaPriceDisplay.textContent = '—';
        lfmPriceDisplay.textContent = '—';
        totalDisplay.textContent = '—';
        areaHidden.value = '';
        calcPriceHidden.value = '';
        unitPriceHidden.value = '';
        lfmTotalHidden.value = '';
        updatePreview(0, 0, false);
        return;
      }

      const { w, h, areaSqm } = result;

      // Price per m² (variant price is in cents)
      const pricePerm2Cents = v.price;
      const areaPriceCents = Math.round(areaSqm * pricePerm2Cents);

      // LFM surcharge: perimeter in mm → m, × lfm_price (€) → cents
      let lfmCents = 0;
      if (v.has_lfm && v.lfm_price > 0) {
        const perimeterM = 2 * (w + h) / 1000;
        lfmCents = Math.round(perimeterM * v.lfm_price * 100);
      }

      // Mindestflächen-Zuschlag
      let minAreaSurchargeCents = 0;
      const minArea = v.has_min_area ? v.min_area : 0;
      if (minArea > 0 && areaSqm < minArea) {
        minAreaSurchargeCents = Math.round((minArea - areaSqm) * pricePerm2Cents);
      }

      const totalPerPieceCents = areaPriceCents + minAreaSurchargeCents + lfmCents;

      // Update displays
      areaDisplay.textContent = areaSqm.toFixed(2).replace('.', ',') + ' m²';
      ppm2Display.textContent = fmt(pricePerm2Cents, currency) + '/m²';
      areaPriceDisplay.textContent = fmt(areaPriceCents, currency);

      // Mindestflächen-Zuschlag display
      if (minAreaSurchargeCents > 0) {
        minAreaPriceDisplay.textContent = fmt(minAreaSurchargeCents, currency);
      } else {
        minAreaPriceDisplay.textContent = '\u2014';
      }

      if (v.has_lfm && v.lfm_price > 0) {
        lfmPriceDisplay.textContent = fmt(lfmCents, currency);
      }

      totalDisplay.textContent = fmt(totalPerPieceCents, currency);

      // Update hidden inputs
      areaHidden.value = areaSqm.toFixed(4);
      calcPriceHidden.value = (totalPerPieceCents / 100).toFixed(2);
      unitPriceHidden.value = (pricePerm2Cents / 100).toFixed(2);
      lfmTotalHidden.value = lfmCents > 0 ? (lfmCents / 100).toFixed(2) : '';

      // Update 2D preview
      updatePreview(w, h, result.valid);
    }

    /* ── Bind events ── */
    [wInput, hInput].forEach(input => {
      input.addEventListener('input', recalc);
      input.addEventListener('change', recalc);
    });

    // Quantity changes
    const qtyInput = form && form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener('input', recalc);
      qtyInput.addEventListener('change', recalc);
    }

    // Variant changes
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      const onVariantChange = () => {
        updateLimits();
        recalc();
      };
      if (idInput) {
        idInput.addEventListener('change', onVariantChange);
        new MutationObserver(onVariantChange).observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
      document.addEventListener('variant:change', onVariantChange);
      document.addEventListener('variant:changed', onVariantChange);
      document.addEventListener('product:variant-change', onVariantChange);
    }

    // Initial state
    updateLimits();
    recalc();
  }

  /* ── Boot ── */
  function boot() {
    document.querySelectorAll('.gc-configurator').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
